<script setup lang="ts">
import { ref } from 'vue';
import { useGetters } from '@/composables/useVuexHelpers';

interface SubmenuItem {
  nav: string;
  icon: string;
  headerText: string;
}

defineProps<{
  title?: string;
  subTitle?: string;
  titleIcon?: string;
  buttonIcon?: string;
  buttonText?: string;
  buttonNav?: string;
  buttonAction?: (e: Event) => void;
  tooltip?: string;
  small?: boolean;
  submenu?: SubmenuItem[];
  customIcon?: boolean;
}>();

const { ukTooltip } = useGetters(['ukTooltip']);

const submenuOpen = ref(false);
function toggleSubmenu() {
  submenuOpen.value = !submenuOpen.value;
}
</script>

<template>
  <div
    class="uk-flex uk-flex-middle"
    :class="{
      [titleIcon === 'warning' ? 'uk-flex-center' : 'uk-flex-between']: true,
    }"
  >
    <div>
      <h3 v-if="small" class="uk-text-bold uk-flex">
        <span
          v-if="titleIcon && !customIcon"
          :uk-icon="`icon:${titleIcon}; ratio:1.5`"
          class="uk-flex-grow uk-margin-small-right"
        />
        <img
          v-if="customIcon"
          class="uk-flex-grow uk-margin-small-right"
          style="width: 30px; height: 30px"
          :src="titleIcon"
        />
        {{ title }}{{ subTitle ? ':' : '' }}
        <small v-if="subTitle" class="uk-margin-left">{{ subTitle }}</small>
      </h3>

      <h2
        v-else
        class="uk-text-bold uk-text-size"
        :class="{ 'uk-flex uk-flex-column uk-text-center': titleIcon }"
      >
        <div
          v-if="titleIcon"
          :uk-icon="`icon:${titleIcon}; ratio:3`"
          class="uk-flex-grow uk-margin-small-bottom"
        />
        <span>{{ title }}{{ subTitle ? ':' : '' }}</span>
        <small v-if="subTitle" class="uk-margin-left">{{ subTitle }}</small>
      </h2>
    </div>

    <div class="uk-flex uk-flex-middle">
      <button
        v-if="submenu"
        class="heading-action-button uk-flex uk-flex-center uk-flex-middle uk-button uk-button-padding uk-button-primary uk-padding-small"
        :class="{ 'heading-action-button-small': small }"
        :uk-tooltip="`title: ${tooltip}; pos: left`"
        @click="toggleSubmenu"
      >
        <img :src="`/icons/white/icon-white-${buttonIcon}.svg`" width="30" />
        <span
          v-if="buttonText && buttonText.trim() && !small"
          class="uk-text-bold uk-margin-small-left"
        >
          {{ buttonText }}
        </span>
        <span
          class="uk-text-bolder"
          :class="{ 'uk-margin-small-left': !small }"
          uk-icon="icon: triangle-down; ratio:1.5"
          style="min-width: 20px"
        />
      </button>

      <div uk-dropdown="mode: click" class="uk-dropdown-click">
        <ul class="uk-nav uk-dropdown-nav uk-link-text">
          <router-link
            v-for="(item, idx) in submenu"
            :key="`submenu-${idx}`"
            custom
            v-slot="{ navigate }"
            :to="item.nav"
          >
            <li class="uk-nav-header uk-link-text">
              <a
                class="uk-nav-header uk-link-text uk-text-bold"
                role="link"
                @click="navigate"
                @keypress.enter="() => navigate()"
              >
                <img
                  :src="`/icons/black/icon-black-${item.icon}.svg`"
                  width="30"
                  class="uk-margin-small-right"
                />
                {{ item.headerText }}
              </a>
            </li>
          </router-link>
        </ul>
      </div>

      <button
        v-if="buttonAction"
        class="heading-action-button uk-flex uk-flex-center uk-flex-middle uk-button uk-button-primary uk-padding-small"
        :class="{ 'heading-action-button-small': small }"
        :uk-tooltip="`title: ${tooltip}; pos: left;`"
        @click="buttonAction"
      >
        <img :src="`/icons/white/icon-white-${buttonIcon}.svg`" style="min-width: 30px" />
        <span
          v-if="buttonText && buttonText.trim() && !small"
          class="uk-text-bold uk-margin-small-left"
        >
          {{ buttonText }}
        </span>
      </button>

      <router-link v-else-if="buttonNav" custom v-slot="{ navigate }" :to="buttonNav">
        <a
          :uk-tooltip="ukTooltip(tooltip)"
          class="heading-action-button uk-border-circle uk-flex uk-flex-center uk-flex-middle uk-button uk-button-primary uk-padding-remove"
          :class="{ 'heading-action-button-small': small }"
          role="link"
          @click="navigate"
          @keypress.enter="() => navigate()"
        >
          <img :src="`/icons/white/icon-white-${buttonIcon}.svg`" width="30" />
          <b>+</b>
        </a>
      </router-link>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.heading-action-button {
  z-index: 1;
  border-radius: 4px;

  i {
    flex-shrink: 0;
  }

  img {
    flex-shrink: 0;
  }

  &-small {
    min-height: 2.5rem;
    min-width: 2.5rem;
    max-height: 2.5rem;
    max-width: 2.5rem;
    z-index: 1;
  }
}

.heading-submenu-button {
  transition: all 0.3s ease-in-out;
  opacity: 0;
  height: 2.5rem;
  width: 2.5rem;
  transform: rotate(108deg);

  &.open {
    opacity: 1;
    transform: rotate(0deg);
    transition: all 0.3s ease-in-out;
  }
}

.uk-dropdown-click {
  max-width: 100% !important;
  left: auto !important;
}

.uk-text-size {
  @media only screen and (max-width: 1024px) {
    font-size: $global-xlarge-font-size;
  }
}

.uk-button-padding {
  @media only screen and (max-width: 1024px) {
    padding: 10px !important;
  }
}
</style>
